// InlineEditField — click-to-edit text field used in detail panels.
//
// Renders as a plain "label: value" row until clicked, then swaps in an
// <input> that saves onBlur (or Enter). Cancels on Escape. Shows a small
// inline error if the save handler throws.
//
// Used by CompanyDetailPanel for website / linkedin_url / name edits.

import React, { useState, useRef, useEffect } from 'react';

export default function InlineEditField({
  label,
  value,
  placeholder = '',
  onSave,                  // async (newValue) => void; throw to surface error
  formatValue = (v) => v,  // optional formatter for display mode
  renderValue,             // optional custom display node (overrides formatValue)
  inputType = 'text',
  disabled = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // Re-sync the draft when the row's value updates from outside (e.g. after
  // a research run rewrites website).
  useEffect(() => { setDraft(value || ''); }, [value]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  async function commit() {
    if (saving) return;
    if ((draft || '') === (value || '')) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(draft.trim() ? draft.trim() : null);
      setEditing(false);
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setDraft(value || '');
    setEditing(false);
    setError(null);
  }

  return (
    <div className="flex flex-col gap-0.5 py-1">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="text-slate-400 flex-shrink-0">{label}</span>
        {editing ? (
          <input
            ref={inputRef}
            type={inputType}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit(); }
              if (e.key === 'Escape') { e.preventDefault(); cancel(); }
            }}
            placeholder={placeholder}
            disabled={saving}
            className="flex-1 min-w-0 bg-slate-900 border border-indigo-500/50 rounded px-2 py-1 text-xs text-slate-100 outline-none focus:border-indigo-400"
          />
        ) : (
          <button
            type="button"
            disabled={disabled}
            onClick={() => setEditing(true)}
            className="flex-1 min-w-0 text-right truncate hover:text-indigo-300 cursor-text"
            title="Click to edit"
          >
            {renderValue
              ? renderValue(value)
              : value
                ? <span className="text-slate-200">{formatValue(value)}</span>
                : <span className="text-slate-600 italic">click to set</span>}
          </button>
        )}
      </div>
      {error && (
        <div className="text-[11px] text-red-400 text-right">{error}</div>
      )}
      {saving && !error && (
        <div className="text-[11px] text-slate-500 text-right">Saving…</div>
      )}
    </div>
  );
}
